// The P24 app supplies its own merchant config and widget types. Only its
// constructor is routed here so prices can follow the product configurator.
window.PDPInstallmentCalculator = class {
  static readAppConfig(source) {
    const literal = source.match(/\bconst\s+config\s*=\s*\{([^{}]*)\}/)?.[1];
    if (!literal) throw new Error("P24 app configuration missing");
    const config = {};
    // The installed app emits primitive fields, not JSON. Parse only that
    // contract; never evaluate JavaScript fetched from the default view.
    for (const entry of literal.split(",")) {
      const match = entry.trim().match(/^(sign|posid|method|amount|currency|lang|test|cms)\s*:\s*(?:'([^'\\]*)'|"([^"\\]*)"|(true|false|\d+))$/);
      if (!match) throw new Error("Unsupported P24 app configuration");
      config[match[1]] = match[2] ?? match[3] ?? JSON.parse(match[4]);
    }
    if (!["sign", "posid", "method", "currency", "lang", "cms"].every((key) => typeof config[key] === "string" && config[key]) || typeof config.test !== "boolean") {
      throw new Error("Incomplete P24 app configuration");
    }
    return config;
  }

  static async loadSDK(source) {
    if (typeof window.InstallmentCalculatorApp === "function") return;
    const url = new URL(source);
    if (url.href !== "https://apm.przelewy24.pl/installments/installment-calculator-app.umd.sdk.js") {
      throw new Error("Unexpected P24 SDK URL");
    }
    this.sdkReady ||= new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = url.href;
      script.onload = () => typeof window.InstallmentCalculatorApp === "function" ? resolve() : reject(new Error("P24 SDK unavailable"));
      script.onerror = () => reject(new Error("P24 SDK failed to load"));
      document.head.appendChild(script);
    });
    await this.sdkReady;
  }

  static async initializeFallback(wrapper) {
    const appBlock = wrapper.querySelector(".shopify-app-block");
    if (!appBlock || appBlock.innerHTML.trim() || wrapper.dataset.pdpInstallmentsFallback) return;
    wrapper.dataset.pdpInstallmentsFallback = "loading";
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("view", "default");
      url.searchParams.delete("section_id");
      url.searchParams.delete("sections");
      url.hash = "";
      const response = await fetch(url, { credentials: "same-origin", signal: AbortSignal.timeout(10000) });
      if (!response.ok) throw new Error("P24 default view unavailable");
      const page = new DOMParser().parseFromString(await response.text(), "text/html");
      const productId = wrapper.dataset.pdpInstallments;
      const source = page.querySelector(`[data-pdp-installments="${productId}"] .shopify-app-block`);
      const initializer = Array.from(source?.querySelectorAll("script:not([src])") || []).find((script) => /\bconst\s+config\s*=/.test(script.textContent));
      const sdk = source?.querySelector('script[src*="installment-calculator-app"]');
      if (!initializer || !sdk) throw new Error("P24 default app block unavailable");
      const config = this.readAppConfig(initializer.textContent);
      if (!wrapper.isConnected) return;
      await this.loadSDK(sdk.src);
      if (!wrapper.isConnected) return;

      const widget = document.createElement("div");
      widget.id = "installment-widget";
      appBlock.appendChild(widget);
      if (!document.getElementById("calculator-modal")) {
        const modal = document.createElement("div");
        modal.id = "calculator-modal";
        document.body.appendChild(modal);
      }
      // The fetched amount is not authoritative: the bridge reads the latest
      // configured price from this page, including changes made during fetch.
      const bridge = new this(config, productId);
      bridge.create("calculator-modal").render("calculator-modal");
      bridge.create("max-widget").render("installment-widget");
      wrapper.dataset.pdpInstallmentsFallback = "initialized";
    } catch (error) {
      wrapper.hidden = true;
      wrapper.dataset.pdpInstallmentsFallback = "failed";
      console.error("[PDPInstallments] Default view fallback unavailable", error);
    }
  }

  constructor(config, productId) {
    this.config = config;
    this.productId = String(productId);
    this.wrapper = document.querySelector(`[data-pdp-installments="${this.productId}"]`);
    this.schema = document.querySelector(`[data-pdp-structured-data][data-product-id="${this.productId}"]`);
    this.targets = new Map();
    this.revision = 0;
    this.appliedRevision = -1;
    this.state = this.readPrice();
    this.wrapper?.querySelector("[data-pdp-installments-trigger]")?.addEventListener("click", () => {
      if (this.wrapper.hidden) return;
      const widget = document.getElementById(this.targets.get("max-widget"));
      widget?.querySelector("button")?.click();
    });
    this.handlePrice = (event) => {
      if (String(event.detail.productId) !== this.productId) return;
      const state = this.readPrice();
      if (state.amount === this.state.amount && state.ready === this.state.ready) return;
      this.state = state;
      this.schedule();
    };
    window.addEventListener("pdpPriceChange", this.handlePrice);
  }

  readPrice() {
    const amount = Number(this.schema?.dataset.pdpPriceCents);
    return {
      amount,
      ready: this.schema?.dataset.pdpPriceReady === "true" && Number.isSafeInteger(amount) && amount > 0,
    };
  }

  renderSummary(app) {
    // P24's modal shows only the first bank in data.providers, whereas its
    // native max-widget takes the minimum across all banks. Match the modal.
    // Provider installment amounts, like the product amount, are in cents.
    const data = app.simulatorData?.data;
    const providerAlias = data?.providers?.[0]?.alias;
    if (!providerAlias) throw new Error("P24 provider unavailable");
    const offers = data.installments || [];
    const amounts = offers.map((offer) => (offer.providers || []).find((provider) => provider.provider === providerAlias)?.installment);
    const amount = Math.min(...amounts.filter((value) => Number.isFinite(value) && value > 0));
    if (!Number.isFinite(amount)) throw new Error("P24 installment unavailable");
    const label = this.wrapper.querySelector("[data-pdp-installment-amount]");
    if (label) label.textContent = new Intl.NumberFormat(document.documentElement.lang || this.config.lang, {
      style: "currency", currency: this.config.currency,
    }).format(amount / 100);
  }

  create(type) {
    return {
      render: (containerId) => {
        this.targets.set(type, containerId);
        this.schedule();
      },
      cleanup: () => this.cleanup(type),
    };
  }

  cleanup(type) {
    this.app?.cleanup(type);
    this.targets.delete(type);
    this.schedule();
  }

  schedule() {
    this.revision += 1;
    this.setHidden(true);
    clearTimeout(this.timer);
    // The app registers modal and widget in consecutive awaited calls.
    this.timer = setTimeout(() => this.refresh(), 0);
  }

  setHidden(hidden) {
    if (this.wrapper) this.wrapper.hidden = hidden;
    this.targets.forEach((id, type) => {
      const container = document.getElementById(id);
      if (container) container.hidden = hidden;
      if (hidden && type === "calculator-modal") {
        const modal = container?.querySelector("#installment-calculator-modal");
        if (modal) modal.style.display = "none";
      }
    });
  }

  async clear() {
    if (!this.app) return;
    this.targets.forEach((id, type) => this.app.cleanup(type));
    // SDK cleanup defers React unmount to the next animation frame.
    await new Promise((resolve) => requestAnimationFrame(resolve));
    this.app = null;
    this.amount = null;
  }

  async refresh() {
    if (this.running) return;
    this.running = true;
    try {
      while (this.appliedRevision !== this.revision) {
        const revision = this.revision;
        this.appliedRevision = revision;
        if (!this.wrapper?.isConnected) {
          await this.clear();
          window.removeEventListener("pdpPriceChange", this.handlePrice);
          return;
        }
        if (!this.state.ready) continue;
        if (!this.targets.size) {
          await this.clear();
          continue;
        }
        if (this.app && this.amount === this.state.amount) {
          this.setHidden(false);
          continue;
        }

        await this.clear();
        if (revision !== this.revision) continue;
        const amount = this.state.amount;
        const app = new InstallmentCalculatorApp({ ...this.config, amount });
        this.app = app;
        for (const [type, id] of this.targets) {
          const component = await app.create(type);
          if (revision !== this.revision) break;
          // The installed SDK catches HTTP errors and otherwise renders an
          // infinite installment when no simulation was returned.
          if (!app.simulatorData) throw new Error("P24 simulation unavailable");
          component.render(id);
        }
        // Let the native React button mount before exposing its theme trigger.
        await new Promise((resolve) => requestAnimationFrame(resolve));
        if (revision === this.revision) {
          this.renderSummary(app);
          this.amount = amount;
          this.setHidden(false);
        }
      }
    } catch (error) {
      this.setHidden(true);
      await this.clear();
      console.error("[PDPInstallments] Calculator unavailable", error);
    } finally {
      this.running = false;
      if (this.appliedRevision !== this.revision) this.timer = setTimeout(() => this.refresh(), 0);
    }
  }
};

const initializePDPInstallments = () => {
  document.querySelectorAll("[data-pdp-installments]").forEach((wrapper) => {
    window.PDPInstallmentCalculator.initializeFallback(wrapper);
  });
};
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializePDPInstallments, { once: true });
} else {
  initializePDPInstallments();
}
